// covered-by: frontends/carrie/src/ask-v2/transport/__tests__/mapServerMessages.test.ts
import { type AskPunttMessage } from "@mg/schemas/src/christo/askPuntt";

import { type AskV2ThreadMessage, type AskV2ThreadStatus } from "./types";

const isReviewStatusMessage = (message: AskPunttMessage): boolean =>
  message.role === "assistant" &&
  message.content === "" &&
  (message.agentSteps?.length ?? 0) > 0 &&
  (message.agentSteps ?? []).every(
    (step) =>
      (step as { tool?: string; type?: string }).tool === "thought" ||
      (step as { tool?: string; type?: string }).type === "text",
  );

const isEmptyAssistantPlaceholder = (message: AskPunttMessage): boolean => {
  if (message.role !== "assistant" || isReviewStatusMessage(message)) {
    return false;
  }
  return (
    !(message.content || message.streamingContent) &&
    !(message.agentSteps?.length ?? 0) &&
    !(message.searchProgress?.length ?? 0) &&
    !(message.contentBlocks?.length ?? 0)
  );
};

const shouldHideEmptyAssistantPlaceholder = ({
  message,
  isLast,
}: {
  message: AskPunttMessage;
  isLast: boolean;
}): boolean => {
  if (!isEmptyAssistantPlaceholder(message)) return false;
  // why: keep the live tail so PunttThinking / poll can see an in-flight turn
  return !isLast;
};

function mapAssistantStatus(message: AskPunttMessage): AskV2ThreadStatus {
  if (message.status === "pending") return "streaming";
  // why: completed/errored empty tails are terminal — do not keep history polling
  if (message.status === "completed") return "sent";
  if (
    !message.content &&
    !message.streamingContent &&
    !isReviewStatusMessage(message)
  ) {
    return "streaming";
  }
  return "sent";
}

function mapServerMessage(message: AskPunttMessage): AskV2ThreadMessage {
  const content = message.streamingContent ?? message.content ?? "";
  if (message.role === "user") {
    return {
      clientId: message._id,
      serverId: message._id,
      role: "user",
      content,
      status: "sent",
      createdAt: message.createdAt,
      userId: message.userId,
      quotedContexts: message.quotedContexts,
      commentContexts: message.commentContexts,
      mentions: message.mentions,
      mentionEmails: message.mentionEmails,
    };
  }

  return {
    clientId: message._id,
    serverId: message._id,
    role: "assistant",
    content,
    status: mapAssistantStatus(message),
    createdAt: message.createdAt,
  };
}

export function mapServerMessages(
  messages: AskPunttMessage[],
  _presentation: "chat" | "feed" = "chat",
): AskV2ThreadMessage[] {
  const visible = messages.filter((message) => !message.hidden);
  const mapped: AskV2ThreadMessage[] = [];

  visible.forEach((message, index) => {
    const isLast = index === visible.length - 1;
    if (shouldHideEmptyAssistantPlaceholder({ message, isLast })) {
      return;
    }
    mapped.push(mapServerMessage(message));
  });

  return mapped;
}

export function hasStreamingServerMessages(
  messages: AskV2ThreadMessage[],
): boolean {
  return messages.some((message) => message.status === "streaming");
}

/**
 * Warhol answers whole on history, with no pending assistant row. Poll while
 * the last visible turn is still the user (or a rembrandt stream is live).
 */
export function shouldPollAskV2History(
  messages: AskV2ThreadMessage[],
  inFlight: boolean,
): boolean {
  if (inFlight) return true;
  if (hasStreamingServerMessages(messages)) return true;
  return messages[messages.length - 1]?.role === "user";
}

export function rawHistoryNeedsPoll(
  messages: AskPunttMessage[],
  inFlight: boolean,
): boolean {
  if (inFlight) return true;
  const visible = messages.filter((message) => !message.hidden);
  if (
    visible.some(
      (message) => message.role === "assistant" && message.status === "pending",
    )
  ) {
    return true;
  }
  return visible[visible.length - 1]?.role === "user";
}

export const HISTORY_POLL_MS = 2000;
export const HISTORY_POLL_MAX_MS = 2 * 60 * 1000;
// why: a warhol artwork review is one turn of 5-15 minutes; the rembrandt
// 2-minute cap would stop the history poll that IS the delivery.
export const WARHOL_HISTORY_POLL_MAX_MS = 30 * 60 * 1000;

export function nextAskV2HistoryPollMs(input: {
  queryStatus: string;
  messages: AskPunttMessage[];
  inFlight: boolean;
  pollStartedAt: number | null;
  now: number;
  agent?: string | null;
}): { interval: number | false; pollStartedAt: number | null } {
  if (input.queryStatus === "error") {
    return { interval: false, pollStartedAt: null };
  }
  if (!rawHistoryNeedsPoll(input.messages, input.inFlight)) {
    return { interval: false, pollStartedAt: null };
  }
  const pollStartedAt = input.pollStartedAt ?? input.now;
  const maxMs =
    input.agent === "warhol" ? WARHOL_HISTORY_POLL_MAX_MS : HISTORY_POLL_MAX_MS;
  if (input.now - pollStartedAt >= maxMs) {
    if (input.agent === "warhol") {
      return { interval: false, pollStartedAt };
    }
    // why: keep polling long reviews — roll the budget instead of freezing off.
    return { interval: HISTORY_POLL_MS, pollStartedAt: input.now };
  }
  return { interval: HISTORY_POLL_MS, pollStartedAt };
}
